import * as THREE from 'three';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';
import {computeWorldBoundsExcludingAnimatedDoorParts} from 'components/customer/RoomPlanner/helpers/calculations';
import {getWallSurfaceBox} from 'components/customer/RoomPlanner/helpers/wallProximity';
import {buildCabinetWallContext} from 'components/customer/RoomPlanner/helpers/measurement/context';
import {
    CabinetWallContext,
    RoomConfig,
    WALL_OFFSETS,
    WallDistances,
    WallRefId,
} from 'components/customer/RoomPlanner/helpers/measurement/types';

/**
 * Calculate the distances from a cabinet to all four walls (left, right, front, back).
 *
 * Returns distances in **scaled units** (i.e. the same unit used for arrow lengths).
 * To convert to mm, divide by `scale`.
 *
 * Usage:
 *   const distances = calculateCabinetWallDistances(cabinetGroup, roomConfig);
 *   const leftMm = Math.round(Math.abs(distances.left) / scale);
 *
 * @param {THREE.Group} cabinetGroup - The cabinet group to measure from
 * @param {RoomConfig} roomConfig - Room dimensions and type
 * @param {boolean} isforwall - Will check if this is for what type wallemasurement or the distance
 * @param {WallRefId} wallRefId - The wall reference ID
 * @return {WallDistances} The distances to each wall in scaled units
 */
export const calculateCabinetWallDistances = (
    cabinetGroup: THREE.Group,
    roomConfig: RoomConfig,
    isforwall?: boolean,
    wallRefId?: WallRefId
): WallDistances => {
    const ctx = buildCabinetWallContext(cabinetGroup, roomConfig);
    return calculateDistancesFromContext(ctx, isforwall, wallRefId);
};

const roomHalfDims = (
    roomWidth: number,
    roomLength: number,
    effectiveRoomLength: number,
    effectiveRoomWidth: number,
    scale: number
) => ({
    halfRoomW: (roomWidth * scale) / 2,
    halfRoomL: (roomLength * scale) / 2,
    halfEffL: (effectiveRoomLength * scale) / 2,
    halfEffW: (effectiveRoomWidth * scale) / 2,
});

/**
 * Calculates wall-to-cabinet distances (left, right, front, back) from cabinet context.
 * for door thickness - matches hasDoors logic in renderCabinetGroup (noInteractions + include_drawer_faces).
 * @param {CabinetWallContext} ctx - Cabinet wall context
 * @param {boolean} [isforwall] - Whether calculating for wall
 * @param {WallRefId} [wallRefId] - Wall reference id
 * @return {WallDistances} Distances to walls (left, right, front, back)
 */
export const calculateDistancesFromContext = (
    ctx: CabinetWallContext,
    isforwall?: boolean,
    wallRefId?: WallRefId
): WallDistances => {
    const {
        width,
        depth,
        position,
        currentDirection,
        roomWidth,
        roomLength,
        rightDepth,
        scale,
        roomType,
        cabinetGroup,
    } = ctx;
    const effectiveRoomLength = ctx.effectiveRoomLength;
    const effectiveRoomWidth = ctx.effectiveRoomWidth;
    let left = 0;
    let right = 0;
    let front = 0;
    let back = 0;
    const cabinetExtThickness = cabinetGroup.userData.values as {
        cabinet_ext_thickness?: number;
    };
    const cabinetCarcThickness = cabinetGroup.userData.values as {
        cabinet_carc_thickness?: number;
    };

    const cabinetCarcThicknessValue =
        (cabinetCarcThickness.cabinet_carc_thickness ?? 0) * scale;
    const isLshaped = roomType === 'LSHAPED';

    const {halfRoomW, halfRoomL, halfEffL, halfEffW} = roomHalfDims(
        roomWidth,
        roomLength,
        effectiveRoomLength,
        effectiveRoomWidth,
        scale
    );
    const otherBox = computeWorldBoundsExcludingAnimatedDoorParts(cabinetGroup);

    const leftToWall = halfRoomW + otherBox.min.x - WALL_OFFSETS.primary;
    const rightToWall = halfRoomW - otherBox.max.x + WALL_OFFSETS.primary;
    const isBulkheadComponent = (
        cabinetGroup.userData as {isBulkheadComponent?: boolean}
    )?.isBulkheadComponent;
    const bulkheadOffset = isBulkheadComponent ? cabinetCarcThicknessValue : 0;
    const isKickerLengthProduct = (
        cabinetGroup.userData as {isKickerLengthProduct?: boolean}
    )?.isKickerLengthProduct;
    const kickerLengthOffset = isKickerLengthProduct
        ? (cabinetExtThickness.cabinet_ext_thickness ?? 0) * scale
        : 0;
    const isPanelProduct = (cabinetGroup.userData as {isPanelProduct?: boolean})
        ?.isPanelProduct;
    const panelOffset = isPanelProduct
        ? (cabinetExtThickness.cabinet_ext_thickness ?? 0) * scale
        : 0;
    // Must read the boolean flag — casting userData itself is always truthy
    // and would apply the L-shape carcase offset to every cabinet.
    const isLShapedProduct = Boolean(
        (cabinetGroup.userData as {isLShapedProduct?: boolean})
            ?.isLShapedProduct
    );
    // Distance calculation by room type and cabinet direction:
    // - L-shaped rooms: corner geometry, effective room dims, wallRefId for specific walls
    // - front/left/right: use frontDoorOffset or sideDoorOffset when cabinetHasDoors
    // - back: no door offset (cabinet back faces wall)
    const isManualPanel = (cabinetGroup.userData as {isManualPanel?: boolean})
        ?.isManualPanel;
    const manualPanelOffset = isManualPanel ? 0.2 : 0;
    const walloffSetForProduct = isPanelProduct ? panelOffset : 0;
    const isFillerProduct = (
        cabinetGroup.userData as {isFillerProduct?: boolean}
    )?.isFillerProduct;
    const values = cabinetGroup.userData.values as {
        cabinet_width?: number;
        cabinet_depth?: number;
        cabinet_left_width?: number;
    };

    const isFillerProductOffset = isFillerProduct
        ? (values.cabinet_width ?? 0) * scale
        : 0;

    if (isLshaped) {
        const lshapedOffset = isLShapedProduct ? cabinetCarcThicknessValue : 0;

        const cornerwallPostionX = halfRoomW - halfEffW + width;
        const cornetwallPostionZ = halfRoomL - (rightDepth * scale) / 2 + depth;
        const depthDiff = (roomLength - effectiveRoomLength) * scale;
        const widthDiff = (roomWidth - effectiveRoomWidth) * scale;
        const adjZ = position.z + depthDiff / 2;
        const reverseAdjZ = otherBox.min.z - depthDiff / 2;
        const reverseAdjX = otherBox.max.x - widthDiff / 2;
        const adjX = otherBox.max.x + widthDiff / 2;
        const cabinetDepth = isLShapedProduct
            ? (values.cabinet_left_width ?? 0) * scale + 6.6
            : (values.cabinet_depth ?? 0) * scale;

        if (currentDirection === 'front') {
            const offSetOndoors = WALL_OFFSETS.edgeAlt2;
            const arrowCheckRight = halfRoomW + (position.x - width);
            const arrowDownward = halfEffL - position.z + WALL_OFFSETS.primary;
            front =
                halfRoomL - otherBox.max.z - offSetOndoors - manualPanelOffset;
            back = otherBox.min.z + halfRoomL + offSetOndoors;
            left =
                leftToWall -
                bulkheadOffset -
                walloffSetForProduct -
                manualPanelOffset;
            right = rightToWall + walloffSetForProduct;
            // Notch gap from carcase; lshapedOffset is carcase thickness for
            // L-shape products only (0 for normal cabinets).
            const frontEffZ = halfEffL - adjZ - offSetOndoors + lshapedOffset;
            const rightEffX = halfEffW - adjX + WALL_OFFSETS.primary;
            const backEffZ =
                depthDiff / 2 -
                halfEffL +
                otherBox.min.z +
                WALL_OFFSETS.edgeAlt2;

            if (cornerwallPostionX < arrowCheckRight) {
                if (!isforwall) {
                    front = frontEffZ;
                }
            }
            if (cornetwallPostionZ > arrowDownward) {
                if (isforwall) {
                    if (wallRefId === 'leftWidth2Wall') {
                        back = backEffZ;
                    }
                    if (wallRefId === 'rightDepthWall') {
                        front = frontEffZ;
                    }
                } else {
                    right = rightEffX;
                }
            } else if (isforwall && wallRefId === 'rightDepthWall') {
                front = frontEffZ;
            }
        } else if (currentDirection === 'back') {
            const offSetOndoors = WALL_OFFSETS.edgeAlt2;
            const arrowCheckRight = halfRoomW + (position.x - width);
            const arrowDownward = halfEffL - position.z + WALL_OFFSETS.primary;
            front =
                halfRoomL - otherBox.max.z - offSetOndoors - manualPanelOffset;
            back = otherBox.min.z + halfRoomL + offSetOndoors;
            left =
                leftToWall -
                bulkheadOffset -
                walloffSetForProduct -
                manualPanelOffset;
            right = rightToWall + walloffSetForProduct;
            // Same carcase starting point as front — adjust this block separately.
            const frontEffZ =
                halfEffL - adjZ - offSetOndoors + lshapedOffset - cabinetDepth;
            const rightEffX = halfEffW - adjX + WALL_OFFSETS.primary;
            const backEffZ =
                depthDiff / 2 -
                halfEffL +
                otherBox.min.z +
                WALL_OFFSETS.edgeAlt2;

            if (cornerwallPostionX < arrowCheckRight) {
                if (isforwall) {
                    if (wallRefId === 'rightWidth2Wall') {
                        left =
                            widthDiff / 2 -
                            halfEffW +
                            position.x -
                            width -
                            WALL_OFFSETS.primary;
                    }
                } else {
                    front = frontEffZ;
                }
            }
            if (cornetwallPostionZ > arrowDownward) {
                if (isforwall) {
                    if (wallRefId === 'leftWidth2Wall') {
                        back = backEffZ;
                    }
                    if (wallRefId === 'rightDepthWall') {
                        front = frontEffZ;
                    }
                    if (wallRefId === 'leftDepthWall') {
                        right = rightEffX;
                    }
                } else {
                    right = rightEffX;
                }
            } else if (isforwall) {
                if (wallRefId === 'rightDepthWall') {
                    front = frontEffZ;
                }
                if (wallRefId === 'leftDepthWall') {
                    right = rightEffX;
                }
                if (wallRefId === 'leftWidth2Wall') {
                    back = backEffZ;
                }
            }
        } else if (currentDirection === 'left') {
            const arrowCheckRight = halfRoomW - adjX;
            const arrowDownward = halfRoomL - adjZ + depth;
            right = halfRoomL + otherBox.min.z + WALL_OFFSETS.edgeAlt;
            left = halfRoomL - otherBox.max.z - WALL_OFFSETS.edgeAlt;
            back = leftToWall;
            front = halfRoomW - otherBox.max.x + WALL_OFFSETS.primary;

            const frontEffX = halfEffW - adjX + WALL_OFFSETS.primary;
            const backEffX =
                widthDiff / 2 -
                halfEffW +
                otherBox.min.x -
                WALL_OFFSETS.primary;
            if (cornerwallPostionX < arrowCheckRight) {
                if (isforwall) {
                    if (wallRefId === 'rightWidth2Wall') {
                        back = halfEffW + reverseAdjX - WALL_OFFSETS.primary;
                    }
                    if (wallRefId === 'leftDepthWall') {
                        front = frontEffX;
                    }
                }
            } else {
                if (isforwall) {
                    if (wallRefId === 'leftDepthWall') {
                        front = frontEffX;
                    }
                    if (wallRefId === 'rightWidth2Wall') {
                        back = backEffX;
                        left = halfEffL - adjZ - WALL_OFFSETS.edge;
                    }
                } else {
                    left = halfEffL - adjZ - WALL_OFFSETS.edge;
                }
            }
            if (cornetwallPostionZ > arrowDownward) {
                if (isforwall) {
                    if (wallRefId === 'rightWidth2Wall') {
                        back = backEffX;
                    }
                } else {
                    front = frontEffX;
                }
            }
        } else if (currentDirection === 'right') {
            const arrowCheckRight = halfRoomW - adjX;
            const arrowDownward = halfRoomL - adjZ + depth;
            left = halfRoomL + otherBox.min.z + WALL_OFFSETS.edgeAlt;
            right = halfRoomL - otherBox.max.z - WALL_OFFSETS.edgeAlt;
            front = leftToWall;
            back = rightToWall;
            const backEffXRight = halfEffW - adjX + WALL_OFFSETS.primary;
            if (cornerwallPostionX < arrowCheckRight) {
                if (isforwall) {
                    if (wallRefId == 'rightWidth2Wall') {
                        front =
                            widthDiff / 2 -
                            halfEffW +
                            position.x -
                            WALL_OFFSETS.primary;
                    }
                    if (wallRefId === 'rightDepthWall') {
                        right = halfEffL - adjZ - WALL_OFFSETS.edge - depth;
                    }
                }
            } else {
                if (isforwall) {
                    if (wallRefId === 'rightDepthWall') {
                        right = halfEffL - adjZ - WALL_OFFSETS.edgeAlt - depth;
                    }
                    if (wallRefId === 'leftWidth2Wall') {
                        right =
                            halfEffL -
                            reverseAdjZ -
                            depth -
                            WALL_OFFSETS.edgeAlt;
                    }
                    if (wallRefId == 'rightWidth2Wall') {
                        front =
                            widthDiff / 2 -
                            halfEffW +
                            otherBox.min.x -
                            WALL_OFFSETS.primary;
                    }
                } else {
                    right = halfEffL - adjZ - WALL_OFFSETS.edge - depth;
                }
            }
            if (cornetwallPostionZ > arrowDownward) {
                if (isforwall) {
                    if (wallRefId === 'leftWidth2Wall') {
                        right =
                            halfEffL -
                            reverseAdjZ -
                            depth -
                            WALL_OFFSETS.edgeAlt;
                        left =
                            depthDiff / 2 -
                            halfEffL +
                            position.z +
                            WALL_OFFSETS.edgeAlt;
                    } else if (wallRefId === 'leftDepthWall') {
                        back = backEffXRight;
                    }
                } else {
                    back = backEffXRight;
                }
            } else {
                if (isforwall && wallRefId === 'leftDepthWall') {
                    back = backEffXRight;
                }
            }
        }
    } else {
        const halfRoomW = (roomWidth * scale) / 2;
        const halfRoomL = (roomLength * scale) / 2;
        if (currentDirection === 'front') {
            const offSetOndoors = WALL_OFFSETS.edgeAlt;

            front =
                halfRoomL - otherBox.max.z - offSetOndoors - manualPanelOffset;

            back = otherBox.min.z + halfRoomL + offSetOndoors;
            left =
                otherBox.min.x +
                halfRoomW -
                WALL_OFFSETS.primary -
                bulkheadOffset -
                kickerLengthOffset -
                walloffSetForProduct -
                manualPanelOffset -
                isFillerProductOffset;
            right =
                halfRoomW -
                otherBox.max.x +
                WALL_OFFSETS.primary -
                manualPanelOffset;
        } else if (currentDirection === 'left') {
            right = halfRoomL + otherBox.min.z + WALL_OFFSETS.edgeAlt;
            left = halfRoomL - otherBox.max.z - WALL_OFFSETS.edgeAlt;
            back = halfRoomW + otherBox.min.x - WALL_OFFSETS.primary;
            front = halfRoomW - otherBox.max.x + WALL_OFFSETS.primary;
        } else if (currentDirection === 'right') {
            left = halfRoomL + otherBox.min.z + WALL_OFFSETS.edgeAlt;
            right = halfRoomL - otherBox.max.z - WALL_OFFSETS.edgeAlt;
            front = halfRoomW + otherBox.min.x - WALL_OFFSETS.primary;
            back = halfRoomW - otherBox.max.x + WALL_OFFSETS.primary;
        } else if (currentDirection === 'back') {
            const offSetOndoors = WALL_OFFSETS.edgeAlt2;
            back = halfRoomL + otherBox.min.z + offSetOndoors;
            front = halfRoomL - otherBox.max.z - offSetOndoors;
            left = halfRoomW + otherBox.min.x - WALL_OFFSETS.primary;
            right = halfRoomW - otherBox.max.x + WALL_OFFSETS.primary;
        }
    }
    return {left, right, front, back};
};
/**
 * Get the distance for a single wall direction.
 * Convenience wrapper around calculateCabinetWallDistances.
 *
 * @param {THREE.Group} cabinetGroup - The cabinet THREE.Group
 * @param {RoomConfig} roomConfig - Room dimensions and type
 * @param {string} wall - Which wall: 'left' | 'right' | 'front' | 'back'
 * @return {number} The distance in scaled units (divide by scale to get mm)
 */
export const getDistanceFromWall = (
    cabinetGroup: THREE.Group,
    roomConfig: RoomConfig,
    wall: 'left' | 'right' | 'front' | 'back'
): number => {
    const distances = calculateCabinetWallDistances(
        cabinetGroup,
        roomConfig,
        false
    );
    switch (wall) {
        case 'left':
            return distances.left;
        case 'right':
            return distances.right;
        case 'front':
            return distances.front;
        case 'back':
            return distances.back;
    }
};

/**
 * Position delta (scene units) that increases `wall` distance by `deltaScaled`.
 * Mirrors the axis relationships in {@link calculateDistancesFromContext}.
 *
 * @param {string} currentDirection - Cabinet orientation (front/back/left/right)
 * @param {'left' | 'right' | 'front' | 'back'} wall - Which distance arrow to change
 * @param {number} deltaScaled - Desired change in scaled units (target − current)
 * @return {{x: number, z: number}} Translation to add to cabinet.position
 */
export const getWallDistancePositionDelta = (
    currentDirection: string,
    wall: 'left' | 'right' | 'front' | 'back',
    deltaScaled: number
): {x: number; z: number} => {
    const move = {x: 0, z: 0};
    if (currentDirection === 'left') {
        if (wall === 'left') move.z = -deltaScaled;
        else if (wall === 'right') move.z = deltaScaled;
        else if (wall === 'front') move.x = -deltaScaled;
        else if (wall === 'back') move.x = deltaScaled;
    } else if (currentDirection === 'right') {
        if (wall === 'left') move.z = deltaScaled;
        else if (wall === 'right') move.z = -deltaScaled;
        else if (wall === 'front') move.x = deltaScaled;
        else if (wall === 'back') move.x = -deltaScaled;
    } else {
        // front, back, or default — left/right on X, front/back on Z
        if (wall === 'left') move.x = deltaScaled;
        else if (wall === 'right') move.x = -deltaScaled;
        else if (wall === 'front') move.z = -deltaScaled;
        else if (wall === 'back') move.z = deltaScaled;
    }
    return move;
};

// A cabinet face this close to a wall (in mm) reads as flush (0 mm).
export const WALL_FLUSH_TOLERANCE_MM = 2;

type ArrowFace = {axis: 'x' | 'z'; side: 'min' | 'max'};
const ARROW_FACE_BY_DIRECTION: Record<string, Record<string, ArrowFace>> = {
    front: {
        back: {axis: 'z', side: 'min'},
        front: {axis: 'z', side: 'max'},
        left: {axis: 'x', side: 'min'},
        right: {axis: 'x', side: 'max'},
    },
    back: {
        back: {axis: 'z', side: 'min'},
        front: {axis: 'z', side: 'max'},
        left: {axis: 'x', side: 'min'},
        right: {axis: 'x', side: 'max'},
    },
    left: {
        left: {axis: 'z', side: 'max'},
        right: {axis: 'z', side: 'min'},
        front: {axis: 'x', side: 'max'},
        back: {axis: 'x', side: 'min'},
    },
    right: {
        left: {axis: 'z', side: 'min'},
        right: {axis: 'z', side: 'max'},
        front: {axis: 'x', side: 'min'},
        back: {axis: 'x', side: 'max'},
    },
};

/**
 * True perpendicular gap (scaled units) between the cabinet face a distance
 * arrow measures and the nearest room wall on that axis. Uses full visible
 * bounds so a door touching the wall reads flush. Only walls running
 * alongside the cabinet count, so it also works in L-shaped rooms.
 * @param {THREE.Group} cabinetGroup - The cabinet group to measure from
 * @param {string} currentDirection - Cabinet orientation (front/back/left/right)
 * @param {string} direction - Which arrow (left/right/front/back)
 * @return {number | null} Gap in scaled units, or null if it can't be measured
 */
export const getTrueWallGapForArrow = (
    cabinetGroup: THREE.Group,
    currentDirection: string,
    direction: string
): number | null => {
    // eslint-disable-next-line security/detect-object-injection -- fixed string keys
    const face = ARROW_FACE_BY_DIRECTION[currentDirection]?.[direction];
    if (!face) return null;

    const scene = cabinetGroup.parent as THREE.Scene | null;
    if (!scene) return null;

    const wallGroup = scene.children.find(
        (ch) =>
            ch.name === ObjectNameMap.rectangularWall ||
            ch.name === ObjectNameMap.lShapeWall
    ) as THREE.Group | undefined;
    if (!wallGroup) return null;

    cabinetGroup.updateWorldMatrix(true, true);
    const cab = new THREE.Box3().setFromObject(cabinetGroup);
    if (cab.isEmpty()) return null;

    const cabFace =
        face.side === 'min'
            ? face.axis === 'x'
                ? cab.min.x
                : cab.min.z
            : face.axis === 'x'
              ? cab.max.x
              : cab.max.z;

    let nearest: number | null = null;
    wallGroup.children.forEach((child) => {
        if (!(child.name.includes('wall:') || child.name.includes('Wall'))) {
            return;
        }
        const wall = child as THREE.Mesh;
        if (!wall.isMesh || !wall.geometry) return;
        const wallBox = getWallSurfaceBox(wall);
        if (wallBox.isEmpty()) return;

        const sizeX = wallBox.max.x - wallBox.min.x;
        const sizeZ = wallBox.max.z - wallBox.min.z;
        const thin: 'x' | 'z' = sizeX < sizeZ ? 'x' : 'z';
        if (thin !== face.axis) return;

        // Only walls running alongside the cabinet: an L-shaped room's
        // notch walls share a plane with parts of the floor they don't
        // border.
        const runsAlongside =
            face.axis === 'x'
                ? wallBox.max.z > cab.min.z && wallBox.min.z < cab.max.z
                : wallBox.max.x > cab.min.x && wallBox.min.x < cab.max.x;
        if (!runsAlongside) return;

        const surfMin = face.axis === 'x' ? wallBox.min.x : wallBox.min.z;
        const surfMax = face.axis === 'x' ? wallBox.max.x : wallBox.max.z;
        const gap = Math.min(
            Math.abs(cabFace - surfMin),
            Math.abs(cabFace - surfMax)
        );
        if (nearest === null || gap < nearest) nearest = gap;
    });

    return nearest;
};

type RoomWall = 'left' | 'right' | 'front' | 'back';
/**
 * Maps a wall reference ID to the physical room wall it represents.
 *
 * Rectangular walls map directly.
 * L-shaped wall segments map to the closest equivalent room wall:
 *   - leftWidthWall  (bottom horizontal)  = back
 *   - rightWidthWall (top horizontal)     = front
 *   - leftDepthWall  (left vertical)      = left
 *   - rightDepthWall (right vertical)     = right
 *   - leftWidth2Wall (inner upper-left)   = front
 *   - rightWidth2Wall (inner lower-right) = back
 *
 * @param {WallRefId} wallRefId - The wall reference ID
 * @return {RoomWall} The physical room wall
 */
const wallRefToRoomWall = (wallRefId: WallRefId): RoomWall => {
    switch (wallRefId) {
        case 'backWall':
            return 'back';
        case 'frontWall':
            return 'front';
        case 'leftWall':
            return 'left';
        case 'rightWall':
            return 'right'; // lshaped walls
        case 'leftWidthWall':
            return 'left';
        case 'leftWidth2Wall':
        case 'rightDepthWall':
            return 'right';
        case 'rightWidthWall':
            return 'back';
        case 'rightWidth2Wall':
        case 'leftDepthWall':
            return 'front';
    }
};
/**
 * Resolve which WallDistances field to use for a given room wall,
 * accounting for the cabinet's direction.
 *
 * calculateDistancesFromContext returns distances using cabinet-relative axes
 * for the 'left' and 'right' cabinet directions (see comments in that function):
 *
 *   direction 'front' / 'back':
 *     left=leftWall, right=rightWall, front=frontWall, back=backWall
 *
 *   direction 'left':
 *     left=frontWall, right=backWall, front=leftWall, back=rightWall
 *
 *   direction 'right':
 *     left=backWall, right=frontWall, front=rightWall, back=leftWall
 *
 * @param {WallDistances} distances - Pre-calculated distances
 * @param {string} direction - Cabinet direction (front/back/left/right)
 * @param {RoomWall} roomWall - Which room wall to get the distance for
 * @return {number} The resolved distance value
 */
const resolveRoomWallDistance = (
    distances: WallDistances,
    direction: string,
    roomWall: RoomWall
): number => {
    if (direction === 'left') {
        // Rotated 90deg: left=front, right=back, front=left, back=right
        switch (roomWall) {
            case 'left':
                return distances.back;
            case 'right':
                return distances.front;
            case 'front':
                return distances.left;
            case 'back':
                return distances.right;
        }
    } else if (direction === 'right') {
        // Rotated -90deg: left=back, right=front, front=right, back=left
        switch (roomWall) {
            case 'left':
                return distances.front;
            case 'right':
                return distances.back;
            case 'front':
                return distances.right;
            case 'back':
                return distances.left;
        }
    } // direction 'front', 'back', or default: identity mapping
    switch (roomWall) {
        case 'left':
            return distances.left;
        case 'right':
            return distances.right;
        case 'front':
            return distances.front;
        case 'back':
            return distances.back;
    }
};
/**
 * Get the distance from a cabinet to a specific room wall, identified by
 * wall reference ID. Handles the direction-based coordinate mapping
 * internally so callers can work in room coordinates.
 *
 * Works for both rectangular and L-shaped rooms.
 *
 * @param {THREE.Group} cabinetGroup - The cabinet group
 * @param {RoomConfig} roomConfig - Room dimensions and type
 * @param {WallRefId} wallRefId - Which wall to measure to
 * @return {number} Distance in scaled units (divide by scale for mm)
 */
export const getDistanceForWall = (
    cabinetGroup: THREE.Group,
    roomConfig: RoomConfig,
    wallRefId?: WallRefId
): number => {
    const distances = calculateCabinetWallDistances(
        cabinetGroup,
        roomConfig,
        false
    );
    const direction =
        (cabinetGroup.userData.currentDirection as string) || 'front';
    const roomWall: RoomWall = wallRefToRoomWall(wallRefId);
    return resolveRoomWallDistance(distances, direction, roomWall);
};
/**
 * Get the left gap and right gap distances for a cabinet relative to a
 * specific wall. The "left gap" is the distance from the wall's left end
 * to the cabinet, and the "right gap" is the distance from the cabinet
 * to the wall's right end.
 *
 * The wall's "left" and "right" depend on which wall:
 *   - backWall / frontWall / widthWalls: left = left room wall, right = right room wall
 *   - leftWall / rightWall / depthWalls: left = back room wall, right = front room wall
 *
 * @param {THREE.Group} cabinetGroup - The cabinet group
 * @param {RoomConfig} roomConfig - Room dimensions and type
 * @param {WallRefId} wallRefId - Which wall to measure along
 * @param {boolean} isforwall - Will check if this is for what type wallemasurement or the distance
 * @return {{leftGap: number, rightGap: number}} Gaps in scaled units
 */
export const getWallGaps = (
    cabinetGroup: THREE.Group,
    roomConfig: RoomConfig,
    wallRefId: WallRefId,
    isforwall: boolean
): {leftGap: number; rightGap: number} => {
    const distances = calculateCabinetWallDistances(
        cabinetGroup,
        roomConfig,
        isforwall,
        wallRefId
    );
    const direction =
        (cabinetGroup.userData.currentDirection as string) || 'front'; // Horizontal walls run along the X axis (left-right)
    // Vertical walls run along the Z axis (back-front)
    const isHorizontalWall =
        wallRefId === 'backWall' ||
        wallRefId === 'frontWall' ||
        wallRefId === 'rightWidthWall' ||
        wallRefId === 'leftDepthWall' ||
        wallRefId === 'rightWidth2Wall';
    if (isHorizontalWall) {
        // Wall runs left-right (X axis)
        return {
            leftGap: resolveRoomWallDistance(distances, direction, 'left'),
            rightGap: resolveRoomWallDistance(distances, direction, 'right'),
        };
    } else {
        // Wall runs back-front (Z axis)
        return {
            leftGap: resolveRoomWallDistance(distances, direction, 'back'),
            rightGap: resolveRoomWallDistance(distances, direction, 'front'),
        };
    }
};
